import {
  FileHelpers,
  activeElementShouldCaptureKeys,
  assert,
  compact,
  getGlobalDocument,
  isDefined,
  preventDefault,
  uniq,
  useEditor,
  useMaybeEditor,
  useValue
} from "@tldraw/editor";
import lz from "lz-string";
import { useCallback, useEffect } from "react";
import { defaultHandleExternalTextContent } from "../../defaultExternalContentHandlers.mjs";
import { TLDRAW_CUSTOM_PNG_MIME_TYPE, getCanonicalClipboardReadType } from "../../utils/clipboard.mjs";
import { useUiEvents } from "../context/events.mjs";
import { pasteFiles } from "./clipboard/pasteFiles.mjs";
import { pasteUrl } from "./clipboard/pasteUrl.mjs";
function resolvePasteModifiers(isShift, isAlt, pasteAtCursorPref) {
  return {
    isPlainText: isShift,
    pasteAtCursor: isAlt ? !pasteAtCursorPref : pasteAtCursorPref
  };
}
const expectedPasteFileMimeTypes = [
  TLDRAW_CUSTOM_PNG_MIME_TYPE,
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml"
];
function stripHtml(html) {
  const doc = getGlobalDocument().implementation.createHTMLDocument("");
  doc.documentElement.innerHTML = html.trim();
  return doc.body.textContent || doc.body.innerText || "";
}
function extractIframeFromHtml(html) {
  if (!html.includes("<iframe")) return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const iframe = doc.querySelector("iframe");
  if (!iframe) return null;
  const src = iframe.getAttribute("src");
  if (!src || !isValidHttpURL(src)) return null;
  const attrWidth = parseInt(iframe.getAttribute("width") || "", 10);
  const attrHeight = parseInt(iframe.getAttribute("height") || "", 10);
  let styleWidth = NaN;
  let styleHeight = NaN;
  const style = iframe.getAttribute("style");
  if (style) {
    const wMatch = style.match(/\bwidth:\s*(\d+)px/);
    const hMatch = style.match(/\bheight:\s*(\d+)px/);
    if (wMatch) styleWidth = parseInt(wMatch[1], 10);
    if (hMatch) styleHeight = parseInt(hMatch[1], 10);
  }
  const width = attrWidth || styleWidth || 425;
  const height = attrHeight || styleHeight || 350;
  return { src, width, height };
}
function isValidHttpURL(url) {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
const getValidHttpURLList = (url) => {
  const urls = url.split(/[\n\s]/);
  for (const url2 of urls) {
    try {
      const u = new URL(url2);
      if (!(u.protocol === "http:" || u.protocol === "https:")) {
        return;
      }
    } catch {
      return;
    }
  }
  return uniq(urls);
};
const isSvgText = (text) => {
  return /^<svg/.test(text);
};
function areShortcutsDisabled(editor) {
  return editor.menus.hasAnyOpenMenus() || activeElementShouldCaptureKeys(false, editor.getContainerDocument());
}
import { putPastedExternalContent } from "./clipboard/putPastedContent.mjs";
import { putPastedExternalContent as putPastedExternalContent2 } from "./clipboard/putPastedContent.mjs";
const handleText = (editor, data, point, sources, clipboardPasteSource = "native-event") => {
  const validUrlList = getValidHttpURLList(data);
  if (validUrlList) {
    for (const url of validUrlList) {
      pasteUrl(editor, url, point, sources, clipboardPasteSource);
    }
  } else if (isValidHttpURL(data)) {
    pasteUrl(editor, data, point, sources, clipboardPasteSource);
  } else if (isSvgText(data)) {
    editor.markHistoryStoppingPoint("paste");
    putPastedExternalContent(
      editor,
      {
        type: "svg-text",
        text: data,
        point,
        sources
      },
      { source: clipboardPasteSource, point }
    );
  } else {
    editor.markHistoryStoppingPoint("paste");
    putPastedExternalContent(
      editor,
      {
        type: "text",
        text: data,
        point,
        sources
      },
      { source: clipboardPasteSource, point }
    );
  }
};
async function handlePasteFromEventClipboardData(editor, clipboardData, point) {
  if (editor.getEditingShapeId() !== null) return;
  if (!clipboardData) {
    throw Error("No clipboard data");
  }
  const things = [];
  for (const item of Object.values(clipboardData.items)) {
    switch (item.kind) {
      case "file": {
        things.push({
          type: "file",
          source: new Promise((r) => r(item.getAsFile()))
        });
        break;
      }
      case "string": {
        if (item.type === "text/html") {
          things.push({
            type: "html",
            source: new Promise((r) => item.getAsString(r))
          });
        } else if (item.type === "text/plain") {
          things.push({
            type: "text",
            source: new Promise((r) => item.getAsString(r))
          });
        } else {
          things.push({ type: item.type, source: new Promise((r) => item.getAsString(r)) });
        }
        break;
      }
    }
  }
  return await handleClipboardThings(editor, things, point, "native-event");
}
async function handlePasteFromClipboardApi({
  editor,
  clipboardItems,
  point,
  fallbackFiles,
  clipboardPasteSource
}) {
  const things = [];
  for (const item of clipboardItems) {
    const matchingTypes = expectedPasteFileMimeTypes.filter((t) => item.types.includes(t));
    if (matchingTypes.length > 0) {
      things.push({
        type: "blob",
        source: (async () => {
          for (const type of matchingTypes) {
            const blob = await item.getType(type);
            if (blob.size === 0) continue;
            return FileHelpers.rewriteMimeType(blob, getCanonicalClipboardReadType(type));
          }
          return null;
        })()
      });
    }
    if (item.types.includes("text/html")) {
      things.push({
        type: "html",
        source: (async () => {
          const blob = await item.getType("text/html");
          return await FileHelpers.blobToText(blob);
        })()
      });
    }
    if (item.types.includes("text/uri-list")) {
      things.push({
        type: "url",
        source: (async () => {
          const blob = await item.getType("text/uri-list");
          return await FileHelpers.blobToText(blob);
        })()
      });
    }
    if (item.types.includes("text/plain")) {
      things.push({
        type: "text",
        source: (async () => {
          const blob = await item.getType("text/plain");
          return await FileHelpers.blobToText(blob);
        })()
      });
    }
  }
  if (fallbackFiles?.length && things.length === 1 && things[0].type === "text") {
    things.pop();
    things.push(
      ...fallbackFiles.map((f) => ({ type: "file", source: Promise.resolve(f) }))
    );
  } else if (fallbackFiles?.length && things.length === 0) {
    things.push(
      ...fallbackFiles.map((f) => ({ type: "file", source: Promise.resolve(f) }))
    );
  }
  return await handleClipboardThings(editor, things, point, clipboardPasteSource);
}
async function handleClipboardThings(editor, things, point, clipboardPasteSource) {
  const results = await Promise.all(
    things.filter((t) => t.type !== "file" && t.type !== "blob").map(
      (t) => new Promise((r) => {
        const thing = t;
        const parsed = thing.source.then((text) => {
          const tldrawHtmlComment = text.match(/<div data-tldraw[^>]*>(.*)<\/div>/)?.[1];
          if (tldrawHtmlComment) {
            try {
              let json;
              try {
                json = JSON.parse(tldrawHtmlComment);
              } catch {
                const jsonComment = lz.decompressFromBase64(tldrawHtmlComment);
                if (jsonComment === null) {
                  r({
                    type: "error",
                    data: null,
                    reason: `found tldraw data comment but could not parse`
                  });
                  return;
                }
                json = JSON.parse(jsonComment);
              }
              if (json.type !== "application/tldraw") {
                r({
                  type: "error",
                  data: json,
                  reason: `found tldraw data comment but JSON was of a different type: ${json.type}`
                });
                return;
              }
              if (json.version === 3) {
                try {
                  const otherData = JSON.parse(
                    lz.decompressFromBase64(json.data.otherCompressed) || "{}"
                  );
                  const reconstructedData = {
                    assets: json.data.assets || [],
                    ...otherData
                  };
                  r({ type: "tldraw", data: reconstructedData });
                  return;
                } catch (error) {
                  r({
                    type: "error",
                    data: json,
                    reason: `failed to decompress version 2 clipboard data: ${error}`
                  });
                  return;
                }
              }
              if (json.version === 2) {
                r({ type: "tldraw", data: json.data });
              } else {
                if (typeof json.data === "string") {
                  r({
                    type: "error",
                    data: json,
                    reason: "found tldraw json but data was a string instead of a TLClipboardModel object"
                  });
                  return;
                }
                r({ type: "tldraw", data: json.data });
                return;
              }
            } catch {
              r({
                type: "error",
                data: tldrawHtmlComment,
                reason: "found tldraw json but data was a string instead of a TLClipboardModel object"
              });
              return;
            }
          } else {
            if (thing.type === "html") {
              r({ type: "text", data: text, subtype: "html" });
              return;
            }
            if (thing.type === "url") {
              r({ type: "text", data: text, subtype: "url" });
              return;
            }
            try {
              const json = JSON.parse(text);
              if (json.type === "excalidraw/clipboard") {
                r({ type: "excalidraw", data: json });
                return;
              } else {
                r({ type: "text", data: text, subtype: "json" });
                return;
              }
            } catch {
              r({ type: "text", data: text, subtype: "text" });
              return;
            }
          }
          r({ type: "error", data: text, reason: "unhandled case" });
        });
        parsed.catch((error) => {
          r({ type: "error", data: null, reason: `error reading clipboard data: ${error}` });
        });
      })
    )
  );
  const files = things.filter((t) => t.type === "file" || t.type === "blob");
  if (files.length) {
    if (files.length > editor.options.maxFilesAtOnce) {
      throw Error("Too many files");
    }
    const fileBlobs = compact(await Promise.all(files.map((t) => t.source)));
    return await pasteFiles(editor, fileBlobs, point, results, clipboardPasteSource);
  }
  for (const result of results) {
    if (result.type === "tldraw") {
      editor.markHistoryStoppingPoint("paste");
      putPastedExternalContent(
        editor,
        { type: "tldraw", content: result.data, point },
        { source: clipboardPasteSource, point }
      );
      return;
    }
  }
  for (const result of results) {
    if (result.type === "excalidraw") {
      editor.markHistoryStoppingPoint("paste");
      putPastedExternalContent(
        editor,
        { type: "excalidraw", content: result.data, point },
        { source: clipboardPasteSource, point }
      );
      return;
    }
  }
  for (const result of results) {
    if (result.type === "text" && result.subtype === "html") {
      const rootNode = new DOMParser().parseFromString(result.data, "text/html");
      const bodyNode = rootNode.querySelector("body");
      const iframeInfo = extractIframeFromHtml(result.data);
      if (iframeInfo) {
        editor.markHistoryStoppingPoint("paste");
        editor.putExternalContent({
          type: "embed",
          url: iframeInfo.src,
          point,
          embed: {
            width: iframeInfo.width,
            height: iframeInfo.height,
            doesResize: true
          }
        });
        return;
      }
      const isHtmlSingleLink = bodyNode && Array.from(bodyNode.children).filter((el) => el.nodeType === 1).length === 1 && bodyNode.firstElementChild && bodyNode.firstElementChild.tagName === "A" && bodyNode.firstElementChild.hasAttribute("href") && bodyNode.firstElementChild.getAttribute("href") !== "";
      if (isHtmlSingleLink) {
        const href = bodyNode.firstElementChild.getAttribute("href");
        handleText(editor, href, point, results, clipboardPasteSource);
        return;
      }
      if (!results.some((r) => r.type === "text" && r.subtype !== "html") && result.data.trim()) {
        const html = stripHtml(result.data) ?? "";
        if (html) {
          handleText(editor, stripHtml(result.data), point, results, clipboardPasteSource);
          return;
        }
      }
      if (results.some((r) => r.type === "text" && r.subtype !== "html")) {
        const html = stripHtml(result.data) ?? "";
        if (html) {
          editor.markHistoryStoppingPoint("paste");
          putPastedExternalContent(
            editor,
            {
              type: "text",
              text: html,
              html: result.data,
              point,
              sources: results
            },
            { source: clipboardPasteSource, point }
          );
          return;
        }
      }
    }
    if (result.type === "text" && result.subtype === "text") {
      const iframeInfo = extractIframeFromHtml(result.data);
      if (iframeInfo) {
        editor.markHistoryStoppingPoint("paste");
        editor.putExternalContent({
          type: "embed",
          url: iframeInfo.src,
          point,
          embed: {
            width: iframeInfo.width,
            height: iframeInfo.height,
            doesResize: true
          }
        });
        return;
      }
    }
  }
  for (const result of results) {
    if (result.type === "text" && result.subtype === "url") {
      pasteUrl(editor, result.data, point, results, clipboardPasteSource);
      return;
    }
  }
  for (const result of results) {
    if (result.type === "text" && result.subtype === "text" && result.data.trim()) {
      handleText(editor, result.data, point, results, clipboardPasteSource);
      return;
    }
  }
}
async function handleNativeOrMenuCopy(editor, context = { operation: "copy", source: "menu" }) {
  const nav = editor.getContainerWindow().navigator;
  let content = await editor.resolveAssetsInContent(
    editor.getContentFromCurrentPage(editor.getSelectedShapeIds())
  );
  if (!content) {
    nav?.clipboard?.writeText?.("");
    return true;
  }
  if (editor.options.onBeforeCopyToClipboard) {
    const result = await editor.options.onBeforeCopyToClipboard({ editor, content, ...context });
    if (result === false) return false;
    if (result != null) content = result;
  }
  const { assets, ...otherData } = content;
  const clipboardData = {
    type: "application/tldraw",
    kind: "content",
    version: 3,
    data: {
      assets: assets || [],
      // Plain JSON, no compression
      otherCompressed: lz.compressToBase64(JSON.stringify(otherData))
      // Only compress non-asset data
    }
  };
  const stringifiedClipboard = JSON.stringify(clipboardData);
  if (typeof nav === "undefined") {
    return true;
  }
  const textItems = content.shapes.map((shape) => {
    const util = editor.getShapeUtil(shape);
    return util.getText(shape);
  }).filter(isDefined);
  if (nav.clipboard?.write) {
    const htmlBlob = new Blob([`<div data-tldraw>${stringifiedClipboard}</div>`], {
      type: "text/html"
    });
    let textContent = textItems.join(" ");
    if (textContent === "") {
      textContent = " ";
    }
    const CBI = editor.getContainerWindow().ClipboardItem;
    nav.clipboard.write([
      new CBI({
        "text/html": htmlBlob,
        // What is this second blob used for?
        "text/plain": new Blob([textContent], { type: "text/plain" })
      })
    ]);
  } else if (nav.clipboard?.writeText) {
    nav.clipboard.writeText(`<div data-tldraw>${stringifiedClipboard}</div>`);
  }
  return true;
}
function useMenuClipboardEvents() {
  const editor = useMaybeEditor();
  const trackEvent = useUiEvents();
  const copy = useCallback(
    async function onCopy(source) {
      assert(editor, "editor is required for copy");
      if (editor.getSelectedShapeIds().length === 0) return;
      const didCopy = await handleNativeOrMenuCopy(editor, { operation: "copy", source: "menu" });
      if (didCopy) {
        trackEvent("copy", { source });
      }
    },
    [editor, trackEvent]
  );
  const cut = useCallback(
    async function onCut(source) {
      if (!editor) return;
      if (editor.getSelectedShapeIds().length === 0) return;
      const didCopy = await handleNativeOrMenuCopy(editor, { operation: "cut", source: "menu" });
      if (didCopy) {
        editor.deleteShapes(editor.getSelectedShapeIds());
        trackEvent("cut", { source });
      }
    },
    [editor, trackEvent]
  );
  const paste = useCallback(
    async function onPaste(data, source, point) {
      if (!editor) return;
      if (editor.getEditingShapeId() !== null) return;
      const win = editor.getContainerWindow();
      if (Array.isArray(data) && data[0] instanceof win.ClipboardItem) {
        if (editor.options.onClipboardPasteRaw?.({
          editor,
          source: "clipboard-read",
          clipboardItems: data,
          point
        }) === false) {
          trackEvent("paste", { source: "menu" });
          return;
        }
        handlePasteFromClipboardApi({
          editor,
          clipboardItems: data,
          point,
          clipboardPasteSource: "clipboard-read"
        });
        trackEvent("paste", { source: "menu" });
      } else {
        const nav = win.navigator;
        nav.clipboard.read().then((clipboardItems) => {
          paste(clipboardItems, source, point);
        });
      }
    },
    [editor, trackEvent]
  );
  return {
    copy,
    cut,
    paste
  };
}
function useNativeClipboardEvents() {
  const editor = useEditor();
  const ownerDocument = editor.getContainerDocument();
  const trackEvent = useUiEvents();
  const appIsFocused = useValue("editor.isFocused", () => editor.getInstanceState().isFocused, [
    editor
  ]);
  useEffect(() => {
    if (!appIsFocused) return;
    const copy = async (e) => {
      if (editor.getSelectedShapeIds().length === 0 || editor.getEditingShapeId() !== null || areShortcutsDisabled(editor)) {
        return;
      }
      preventDefault(e);
      const didCopy = await handleNativeOrMenuCopy(editor, { operation: "copy", source: "native" });
      if (didCopy) {
        trackEvent("copy", { source: "kbd" });
      }
    };
    async function cut(e) {
      if (editor.getSelectedShapeIds().length === 0 || editor.getEditingShapeId() !== null || areShortcutsDisabled(editor)) {
        return;
      }
      preventDefault(e);
      const didCopy = await handleNativeOrMenuCopy(editor, { operation: "cut", source: "native" });
      if (didCopy) {
        editor.deleteShapes(editor.getSelectedShapeIds());
        trackEvent("cut", { source: "kbd" });
      }
    }
    let disablingMiddleClickPaste = false;
    const pointerUpHandler = (e) => {
      if (e.button === 1) {
        disablingMiddleClickPaste = true;
        editor.timers.requestAnimationFrame(() => {
          disablingMiddleClickPaste = false;
        });
      }
    };
    let nativeShiftKey = false;
    const trackModifiers = (e) => {
      nativeShiftKey = e.shiftKey;
    };
    const paste = (e) => {
      if (disablingMiddleClickPaste) {
        editor.markEventAsHandled(e);
        return;
      }
      if (editor.getEditingShapeId() !== null || areShortcutsDisabled(editor)) return;
      if (nativeShiftKey) {
        const text = e.clipboardData?.getData("text/plain");
        if (text?.trim()) {
          const point2 = editor.user.getIsPasteAtCursorMode() ? editor.inputs.getCurrentPagePoint() : editor.getViewportPageBounds().center;
          editor.markHistoryStoppingPoint("paste");
          defaultHandleExternalTextContent(editor, { text, point: point2 });
          preventDefault(e);
          trackEvent("paste", { source: "kbd" });
          return;
        }
      }
      const point = editor.user.getIsPasteAtCursorMode() ? editor.inputs.getCurrentPagePoint() : void 0;
      if (editor.options.onClipboardPasteRaw?.({
        editor,
        source: "native-event",
        event: e,
        clipboardData: e.clipboardData,
        point
      }) === false) {
        preventDefault(e);
        trackEvent("paste", { source: "kbd" });
        return;
      }
      const pasteFromEvent = () => {
        if (e.clipboardData) {
          handlePasteFromEventClipboardData(editor, e.clipboardData, point);
        }
      };
      const win = editor.getContainerWindow();
      const nav = win.navigator;
      if (nav.clipboard?.read) {
        const fallbackFiles = Array.from(e.clipboardData?.files || []);
        nav.clipboard.read().then(
          (clipboardItems) => {
            if (Array.isArray(clipboardItems) && clipboardItems[0] instanceof win.ClipboardItem) {
              handlePasteFromClipboardApi({
                editor,
                clipboardItems,
                point,
                fallbackFiles,
                clipboardPasteSource: "native-event"
              });
            }
          },
          () => {
            pasteFromEvent();
          }
        );
      } else {
        pasteFromEvent();
      }
      preventDefault(e);
      trackEvent("paste", { source: "kbd" });
    };
    ownerDocument?.addEventListener("copy", copy);
    ownerDocument?.addEventListener("cut", cut);
    ownerDocument?.addEventListener("paste", paste);
    ownerDocument?.addEventListener("pointerup", pointerUpHandler);
    ownerDocument?.addEventListener("keydown", trackModifiers, true);
    ownerDocument?.addEventListener("keyup", trackModifiers, true);
    return () => {
      ownerDocument?.removeEventListener("copy", copy);
      ownerDocument?.removeEventListener("cut", cut);
      ownerDocument?.removeEventListener("paste", paste);
      ownerDocument?.removeEventListener("pointerup", pointerUpHandler);
      ownerDocument?.removeEventListener("keydown", trackModifiers, true);
      ownerDocument?.removeEventListener("keyup", trackModifiers, true);
    };
  }, [editor, trackEvent, appIsFocused, ownerDocument]);
}
export {
  extractIframeFromHtml,
  handleNativeOrMenuCopy,
  handlePasteFromClipboardApi,
  handlePasteFromEventClipboardData,
  isValidHttpURL,
  putPastedExternalContent2 as putPastedExternalContent,
  resolvePasteModifiers,
  useMenuClipboardEvents,
  useNativeClipboardEvents
};
//# sourceMappingURL=useClipboardEvents.mjs.map
